import type { OpenPanelOptions } from '@navio/observability';
import * as Sentry from '@sentry/react';
import * as ReactQuery from '@tanstack/react-query';
import { noddiAsync } from 'noddi-async';
import { noddiToast } from 'noddi-ui-web';
import {
  getInternalErrorMessage,
  handleMutationSuccess,
  isAxiosError,
  isMutationToastSuppressed,
  isSessionExpiredError
} from 'noddi-util';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { registerNewVersionReload } from './newVersionReload';
import { initObservability } from './observability';
import sentryConfig from './sentry';
import { isDevelopment, isLocal, openPanelSessionReplay, setEnvFavicon } from './src/constants/environment';
import { getStuddedTireSeasonExplanation } from './src/features/BookingFlow/studdedTireSeason';
import { resolveApiBaseUrl } from './src/stores/DevSettingsStore';
import { tracking } from './src/tracking';
import { logQueryCacheError } from './src/utils/logQueryCacheError';

interface BootstrapAppOptions {
  /** Window title prefix, e.g. "Partner" (an env suffix is appended on dev/local). */
  title: string;
  /** noddiAsync request source header for this app. */
  source: 'partner' | 'admin' | 'facility';
  /** Faro/observability app name, e.g. "navio-partner". */
  observabilityName: string;
  /** Build-time release version (`__SOURCE_VERSION__` from the app). */
  sourceVersion: string;
  /** OpenPanel session replay. Defaults to the shared 80% production sample. */
  sessionReplay?: OpenPanelOptions['sessionReplay'];
}

/**
 * Reload the page when a dynamic import fails because its chunk hash no longer
 * exists after a deploy. The sessionStorage flag prevents a reload loop if the
 * page can't recover (offline, stale CDN edge, etc.).
 * Vite recommended pattern: https://vite.dev/guide/build.html#load-error-handling
 */
export const registerPreloadErrorReload = () => {
  window.addEventListener(
    'vite:preloadError',
    (event) => {
      if (sessionStorage.getItem('vite-preload-reloaded') === 'true') {
        return;
      }
      event.preventDefault();
      sessionStorage.setItem('vite-preload-reloaded', 'true');
      window.location.reload();
    },
    { once: true }
  );
};

/**
 * Shared init for the backoffice web apps (partner, admin, facility), which
 * otherwise duplicate an identical bootstrap sequence. Call once from the app's
 * `index.tsx` before rendering. The customer app keeps its own bespoke bootstrap
 * (brand-managed title, different mutation handling). Rendering stays in each app.
 */
export const bootstrapApp = ({
  title,
  source,
  observabilityName,
  sourceVersion,
  sessionReplay = openPanelSessionReplay
}: BootstrapAppOptions) => {
  const envSuffix = isDevelopment ? ' - dev' : isLocal ? ' - local' : '';
  document.title = `${title}${envSuffix}`;
  setEnvFavicon();

  registerPreloadErrorReload();
  // The dev server has no version.json; local always runs the working tree.
  // Same release id as `emitVersionFile` writes (packages/config/vite.config.mts).
  if (!isLocal) {
    registerNewVersionReload(__DEPLOY_META__.DEPLOY_ID ?? sourceVersion);
  }

  noddiAsync.init({
    reactQuery: ReactQuery,
    baseUrl: resolveApiBaseUrl(import.meta.env.VITE_APP_BE_URL ?? ''),
    source,
    sourceVersion,
    // Build age and contract version for the backend's API contract
    // dashboard: which build is live, generated against which nsdk.
    sourceReleasedAt: __SOURCE_RELEASED_AT__,
    sdkVersion: __NSDK_PACKAGE_VERSION__,
    queryCacheConfig: {
      onError: (error) => logQueryCacheError('query', error)
    },
    mutationCacheConfig: {
      onSuccess: (data, _variables, _context, mutation) => {
        // Bulk fan-out wraps work in `runWithoutMutationToasts` and shows one summary.
        if (isMutationToastSuppressed() || mutation.meta?.suppressSuccessToast === true) {
          return;
        }
        handleMutationSuccess(data, noddiToast);
      },
      onError: async (error, _variables, _context, mutation) => {
        // Still log every failure; only the toast is suppressed — for bulk summaries, and for
        // a dialog that shows the error itself, and for an expired session (every request fails at once).
        logQueryCacheError('mutation', error);
        if (isMutationToastSuppressed() || mutation.meta?.suppressErrorToast === true || isSessionExpiredError(error)) {
          return;
        }
        // The studded-tire season refusal is a confirmation the backend asks for, and every booking UI answers
        // it in a dialog. A toast would repeat the same legal text behind an overlay that cannot be read.
        if (getStuddedTireSeasonExplanation(error)) {
          return;
        }

        const errorMessage = getInternalErrorMessage(error);
        const responseData = isAxiosError(error) ? error.response?.data : undefined;
        if (responseData instanceof Blob) {
          try {
            const text = await responseData.text();
            const parsed = JSON.parse(text);
            const detail = parsed?.errors?.[0]?.detail;
            noddiToast.error(detail ?? errorMessage);
          } catch {
            noddiToast.error(errorMessage);
          }
        } else {
          noddiToast.error(errorMessage);
        }
      }
    }
  });

  // Faro RUM first so its global error/console instrumentation is in place before Sentry.
  initObservability(observabilityName, sourceVersion);

  // OpenPanel product analytics (parallel to Faro; no Mixpanel on backoffice apps).
  tracking.initOpenPanel({
    clientId: import.meta.env.VITE_APP_OPENPANEL_CLIENT_ID ?? '',
    apiUrl: import.meta.env.VITE_APP_OPENPANEL_API_URL ?? '',
    // Dashboard project page, so Sentry errors can link to this session's replay.
    projectUrl: import.meta.env.VITE_APP_OPENPANEL_PROJECT_URL,
    // Auto-track clicks on data-track="eventName" (OpenPanel only).
    trackAttributes: true,
    sessionReplay
  });

  Sentry.init({
    ...sentryConfig,
    integrations: [
      ...sentryConfig.integrations,
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      })
    ]
  });
};
